import React, { useState, useEffect, useRef } from "react";
import { AuthorizedLayoutCompanies } from "../../components/AuthorizedLayoutCompanies";
import SearchFilter from "./components/SearchFilter";
import iconSearch from "../../assets/Frame 21 (1).png";
import iconLArrow from "../../assets/Vector (29).png";
import { TalentCard } from "../../components/TalentCard";
import "./TalentSearch.css";
import api from "../../utils/api";
import { toast } from "react-toastify";
import axios from "axios";
import LoadingIcon from "../../components/LoadingIcon";
import { useSearchParams, useNavigate } from "react-router-dom";
import Pagination from "../../components/Companies/Pagination/Pagination";
import { usePlanPermissions } from '../../hooks/usePlanPermissions';
import { useResponsiveItemsPerPage } from '../../hooks/useResponsiveItemsPerPage';
import { usePlan } from '../../providers/PlanProvider';

// Utility function to convert currency string to decimal
function parseCurrencyToDecimal(value) {
  if (!value) return null;
  let clean = value.replace(/[^0-9,.-]/g, '');
  clean = clean.replace(/\./g, '').replace(',', '.');
  const num = parseFloat(clean);
  return isNaN(num) ? null : num;
}

const TalentSearch = () => {
  const [brazilStates, setBrazilStates] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [tagsExpanded, setTagsExpanded] = useState(() => {
    const saved = localStorage.getItem('@ecomlovers:talentCardTagsExpanded');
    return saved ? JSON.parse(saved) : false;
  });

  const handleToggleTagsExpand = () => {
    const newState = !tagsExpanded;
    setTagsExpanded(newState);
    localStorage.setItem('@ecomlovers:talentCardTagsExpanded', JSON.stringify(newState));
  };
  const initialPage = parseInt(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [talents, setTalents] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Hook to calculate items per page responsively
  const { itemsPerPage, recalculate } = useResponsiveItemsPerPage(230, 20, 7);
  const [filters, setFilters] = useState({
    location: {
      state: "",
      cities: "",
    },
    salary: {
      min: "",
      max: "",
      open: false,
    },
    workModel: "",
    contractType: "",
    seniorityLevel: "",
    selfieLetter: false,
    knowledge: [],
    skills: [],
    visibleSkills: {},
    primary: "",
    secondary: "",
    balanced: false,
    searchTerm: "",
  });
  const [filtersReady, setFiltersReady] = useState(false);
  const [isFilterChange, setIsFilterChange] = useState(false);
  const isFilterChangeRef = useRef(false);
  const containerScrollRef = useRef(null);

  const { validatePageNavigation } = usePlanPermissions();
  const { planData, loading: planLoading } = usePlan();

  // Validate URL access for trial users and sync page from URL
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get("page")) || 1;

    // Wait for plan data loading before validation
    if (planLoading) {
      return; // Don't do anything while data is loading
    }

    // If no plan data yet, don't validate
    if (!planData) {
      return;
    }

    // Don't sync page if it's a filter change (to avoid conflicts)
    if (isFilterChange || isFilterChangeRef.current) {
      return;
    }

    if (pageFromUrl > 1) {
      const validation = validatePageNavigation();

      if (!validation.allowed) {
        // Update URL to page 1
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("page", "1");
        navigate(`?${newSearchParams.toString()}`, { replace: true });

        // Set current page to 1
        setCurrentPage(1);
      } else if (pageFromUrl !== currentPage) {
        setCurrentPage(pageFromUrl);
      }
    } else if (pageFromUrl !== currentPage) {
      setCurrentPage(pageFromUrl);
    }
  }, [searchParams, validatePageNavigation, navigate, planData, planLoading, isFilterChange]);

  const handlePageChange = (newPage) => {
    if (newPage === 1) {
      setCurrentPage(newPage);
      // Update URL to page 1
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("page", "1");
      navigate(`?${newSearchParams.toString()}`, { replace: true });
      return;
    }

    const validation = validatePageNavigation();

    if (validation.allowed) {
      setCurrentPage(newPage);
      // Update URL with the new page
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("page", newPage.toString());
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    } else {
      // Don't change the page if validation fails
    }
  };

  const skillaAreas = {
    1: "operation",
    2: "marketing",
    3: "marketplace",
    4: "platform",
    5: "erp",
    6: "designAndMedia",
    7: "dataAndBi",
    8: "socialMedia",
    9: "ia",
    10: "dayToDay",
  };

  const knowledgeAreas = {
    1: "knowledgeSkills",
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const { data } = await axios.get(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
        );
        setBrazilStates(data);
      } catch (error) {
        console.error("Erro ao carregar estados:", error);
      }
    };

    fetchStates();
  }, []);

  const getFavorite = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/talents/filters`);

      const state = searchParams.get("state") || "";
      const citiesParam = searchParams.get("cities");
      const cities = citiesParam ? citiesParam.split(",") : [];
      const workModel = searchParams.get("workModel") || "";
      const contractType = searchParams.get("contractType") || "";
      const seniorityLevel = searchParams.get("seniorityLevel") || "";
      const min = searchParams.get("minSalary") || "";
      const max = searchParams.get("maxSalary") || "";
      const negotiable = searchParams.get("negotiable") === "true";
      const selfieLetter = searchParams.get("selfieLetter") === "true";
      const primary = searchParams.get("primary") || "";
      const secondary = searchParams.get("secondary") || "";
      const balanced = searchParams.get("balanced") === "true";
      const searchTerm = searchParams.get("searchTerm") || "";

      // Ensure page is preserved from URL
      const pageFromUrl = parseInt(searchParams.get("page")) || 1;
      if (pageFromUrl !== currentPage) {
        setCurrentPage(pageFromUrl);
      }

      let knowledgeFromUrl = [];
      let skillsFromUrl = [];
      try {
        knowledgeFromUrl = JSON.parse(searchParams.get("knowledge") || "[]");
        skillsFromUrl = JSON.parse(searchParams.get("skills") || "[]");
      } catch (e) {
        console.error("Erro ao fazer parse de knowledge ou skills:", e);
      }

      if (data.data) {
        const knowledgeList = Object.values(knowledgeAreas).flatMap((area) =>
          data?.data?.attributes?.knowledgeAreas?.[area]?.map((item) => ({
            id: item.id,
            level: 0,
            name: item.name,
            checked: false,
          })) || []
        );

        const skillsList = Object.entries(skillaAreas).map(([_, area]) => ({
          [area]:
            data?.data?.attributes?.knowledgeAreas?.[area]?.map((item) => ({
              id: item.id,
              level: 0,
              name: item.name,
              checked: false,
              logo: item.logo,
            })) || [],
        }));

        const updatedKnowledge = knowledgeList.map((k) => {
          const found = knowledgeFromUrl.find((i) => Number(i.id) === Number(k.id));
          return found ? { ...k, level: found.level ?? 0, checked: true } : { ...k };
        });

        const updatedSkills = skillsList.map((category) => {
          const key = Object.keys(category)[0];
          return {
            [key]: category[key].map((s) => {
              const found = skillsFromUrl.find((i) => Number(i.id) === Number(s.id));
              return found ? { ...s, level: found.level ?? 0, checked: true } : s;
            }),
          };
        });

        setFilters((prev) => ({
          ...prev,
          location: {
            state,
            cities,
          },
          salary: { ...prev.salary, min, max, negotiable },
          workModel,
          contractType,
          seniorityLevel,
          selfieLetter,
          primary,
          secondary,
          balanced,
          searchTerm,
          knowledge: updatedKnowledge,
          skills: updatedSkills,
        }));
        setFiltersReady(true);
      }
    } catch (error) {
      console.error("Erro ao carregar filtros:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFavorite();
  }, []);

  // Recalculate items per page when screen is resized
  useEffect(() => {
    const handleResize = () => {
      const oldItemsPerPage = itemsPerPage;
      recalculate();

      // If the number of items per page changed, recalculate current page
      // to avoid showing an empty page
      if (oldItemsPerPage !== itemsPerPage && talents) {
        const newTotalPages = Math.ceil((talents.length + (oldItemsPerPage - itemsPerPage)) / itemsPerPage);
        if (currentPage > newTotalPages) {
          setCurrentPage(1);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [recalculate, itemsPerPage, talents, currentPage]);

  const fetchTalents = async () => {
    setLoading(true);
    setTalents(null);
    try {
      const { data } = await api.post(`/companies/talents/search`, {
        data: {
          attributes: {
            knowledgeAreas:
              filters?.knowledge
                ?.filter((item) => item.checked)
                ?.map((conh) => ({
                  id: conh.id,
                  level: conh.level,
                })) || [],
            cities: filters?.location?.cities,
            state:
              brazilStates?.find(
                (item) => Number(item.id) === Number(filters?.location?.state)
              )?.sigla || "",
            workModel: filters?.workModel,
            contractType: filters?.contractType,
            seniorityLevel: filters?.seniorityLevel,
            minSalary: parseCurrencyToDecimal(filters?.salary?.min),
            maxSalary: parseCurrencyToDecimal(filters?.salary?.max),
            salaryNegotiable: filters?.salary?.negotiable,
            selfieLetterRequired: filters?.selfieLetter,
            hasProfilePhoto: filters?.hasProfilePhoto,
            predominantFit: filters?.primary,
            secondaryFit: filters?.secondary,
            balancedFit: filters?.balanced,
            skills: filterCheckedSkills(filters.skills),
            searchTerm: filters?.searchTerm,
            page: currentPage,
            perPage: itemsPerPage,
          },
        },
      });

      const talentList = data?.data?.map((item) => ({
        id: item.id,
        ...item.attributes,
      })) || [];

      setTalents(talentList);
      setTotalPages(data?.meta?.totalPages || 1);
      setTotalCount(data?.meta?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching talents:", error);
    }
    setLoading(false);
  };

  // Effect for filters changes (with debounce)
  useEffect(() => {
    if (!filtersReady) return;

    // If it's the first load, fetch immediately
    if (!talents) {
      fetchTalents();
      return;
    }

    // For subsequent changes, use debounce
    const debounce = setTimeout(() => {
      fetchTalents();
    }, 600);

    return () => clearTimeout(debounce);
  }, [filters, filtersReady, itemsPerPage]); // Removed currentPage from dependencies

  // Separate effect for page changes
  useEffect(() => {
    if (!filtersReady || !talents) return;

    // Fetch talents when page changes
    fetchTalents();
  }, [currentPage]); // Only observe currentPage changes

  const handleFilterChange = (category, value) => {
    setFilters((prev) => ({
      ...prev,
      [category]: value,
    }));

    // Mark that it's a filter change using ref and state
    isFilterChangeRef.current = true;
    setIsFilterChange(true);

    // Reset page to 1 when filters change
    setCurrentPage(1);

    // Update URL to remove the page parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("page"); // Remove the page parameter from URL
    navigate(`?${newSearchParams.toString()}`, { replace: true });

    // Reset the flag after a delay
    setTimeout(() => {
      isFilterChangeRef.current = false;
      setIsFilterChange(false);
    }, 500);

  };

  const filterCheckedSkills = (skills) => {
    const result = [];

    // Iterate over each skill category
    for (const category of skills) {
      // For each category, get the category name (first key)
      const categoryName = Object.keys(category)[0];

      // Filter only checked items within this category
      const checkedItems = category[categoryName].filter(
        (item) => item.checked === true
      );

      // Map only id and level of checked items and add to result
      checkedItems.forEach((item) => {
        result.push({
          id: item.id,
          level: item.level,
        });
      });
    }

    return result || null;
  };

  return (
    <AuthorizedLayoutCompanies ref={containerScrollRef}>
      <div className="titlePage">Nossa Comunidade</div>
      <div className="talent-search-container">
        <SearchFilter
          handleFilterChange={handleFilterChange}
          filters={filters}
          setFilters={setFilters}
          filtersReady={filtersReady}
          fetchTalents={() => {
            setCurrentPage(1);
            setTimeout(() => {
              fetchTalents();
            }, 0);
          }}
          brazilStates={brazilStates}
          loading={loading}
          currentPage={currentPage}
          isFilterChange={isFilterChange}
          setIsFilterChange={setIsFilterChange}
        />

        <div className="contentContainer" style={{ flex: 1 }}>
          <div className="conainerRight">
            <div className="talent-list-container" style={{minHeight: '690px'}}>
              <div className="headerTitle">
                <h2 className="title">Lista de talentos</h2>

                <div
                  className="containerFilters"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <div className="text">
                    <strong>
                      {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)}
                    </strong>{" "}
                    de <strong>{totalCount}</strong> talentos
                  </div>
                  <div className="search-container">
                    <input
                      placeholder="Pesquisar talento"
                      variant="outlined"
                      size="small"
                      value={filters?.searchTerm || ""}
                      onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
                    />

                    <img
                      src={iconSearch}
                      alt="sarch"
                      style={{ width: "30px" }}
                    />
                  </div>
                </div>
              </div>

              {loading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "150px",
                    justifySelf: "center",
                    marginTop: "50px",
                  }}
                >
                  <LoadingIcon />
                </div>
              )}


              <div className="talent-list">
                {Array.isArray(talents) && talents.length > 0 ? (
                  talents.map((talent) => (
                    <TalentCard
                      key={talent.id}
                      talent={talent}
                      onTagsUpdate={fetchTalents}
                      tagsExpanded={tagsExpanded}
                      onToggleTagsExpand={handleToggleTagsExpand}
                    />
                  ))
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "3px", width: "100%"}} >
                    <div className="titleContent">Nenhum talento foi encontrado</div>
                    <div className="textContent">
                      Tente ajustar os termos de busca ou experimente outros filtros.
                    </div>
                  </div>
                )}
              </div>

              {talents?.length > 0 && !!currentPage && (
                <Pagination
                  currentPage={Number(currentPage)}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </AuthorizedLayoutCompanies>
  );
};

export default TalentSearch;
